import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { Session } from '@/domain/schema/v1';
import { sessionStage } from '@/domain/workflow/home';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { flushAll } from '../../state/saves';
import { AnalysisSection } from './AnalysisSection';
import { EvidenceSection, GoalProgressSection, RiskSection } from './ClinicalSections';
import { InputSection, MemoBox, SessionInfoFields } from './InputSection';
import { TranscriptSection } from './TranscriptSection';
import { DialogueMaker } from './DialogueMaker';
import { CreatedAt } from '../../components/CreatedAt';

/**
 * 회기 상세: 한 회기의 모든 기록 (MASTER §2.2, UX_FLOW §8~§14).
 * 화면 개편(A34): 위에 목차(가는 길), 아래에 각 칸. 회기 진행 화면의 "자세히"가 여기로 온다.
 */
export function SessionScreen() {
  const { sessionId = '' } = useParams();
  const svc = useService();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    data,
    reload,
    error: loadError,
  } = useLoad(async () => {
    await svc.resumeIfNeeded(sessionId); // 중단된 승인 절차 마무리
    const session = await svc.getSession(sessionId);
    const [client, journal] = await Promise.all([svc.getClient(session.clientId), svc.currentJournal(sessionId)]);
    return { session, client, journal };
  }, [svc, sessionId]);

  useEffect(() => {
    if (data) document.title = `${data.client.clientCode} ${data.session.sessionNumber}회기 · SDCare`;
  }, [data]);
  // 목차·다른 화면에서 #칸 으로 오면 그 칸으로
  const { hash } = useLocation();
  const loaded = !!data;
  useEffect(() => {
    if (!loaded || !hash) return;
    const t = window.setTimeout(
      () => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      50,
    );
    return () => window.clearTimeout(t);
  }, [loaded, hash]);

  if (loadError) return <p role="alert">회기를 찾을 수 없습니다.</p>;
  if (!data) return <p className="muted">불러오는 중…</p>;
  const { session, client, journal } = data;
  const processed = session.status !== 'draft' && session.status !== 'processing';

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await flushAll(); // 화면에 입력 중인 내용을 먼저 저장
      await fn();
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="stack session-hub">
      <div className="page-head">
        <div>
          <p className="eyebrow">
            <Link to={`/clients/${client.id}`}>‹ {client.clientCode}</Link>
          </p>
          <h1 className="page-title">
            {client.clientCode} · {session.sessionNumber}회기
          </h1>
          <p className="muted">
            {session.sessionDate} · {session.durationMinutes ?? '-'}분 · {sessionStage(session).stage}
          </p>
          <CreatedAt iso={session.createdAt} />
        </div>
        <div className="row">
          {session.status !== 'approved' && (
            <Link className="button primary" to={`/sessions/${session.id}/run`}>
              회기 진행 화면 ▶
            </Link>
          )}
          {session.sessionNumber > 1 && (
            <Link to={`/clients/${client.id}/brief?target=${session.sessionNumber}`}>이 회기 준비 자료</Link>
          )}
          <Link to={`/clients/${client.id}`}>내담자 상세</Link>
        </div>
      </div>
      {error && (
        <p role="alert" className="warn">
          {error}
        </p>
      )}

      {!processed ? (
        <>
          <InputSection session={session} onChanged={reload} />
          <div className="card">
            <h2>회기 처리</h2>
            <p className="muted small">
              입력한 자료는 이미 저장되어 있습니다. 다른 기기에서 전사를 올렸다면 동기화 후 이 화면에 두 전사가 모두
              보이는지 확인하고 처리하세요. 처리 후에는 원본 전사를 바꿀 수 없습니다.
            </p>
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() => void run(() => svc.processSession(session.id))}
            >
              {busy
                ? '처리 중…'
                : session.samsungTranscriptId && session.appleTranscriptId
                  ? '회기 처리'
                  : session.samsungTranscriptId || session.appleTranscriptId
                    ? '전사 하나로 다음 단계 진행'
                    : '회기 처리'}
            </button>
          </div>
        </>
      ) : (
        <div className="hub-grid">
          <nav className="list-card hub-index" aria-label="이 회기 기록">
            <HubRow to="#info" icon="정" title="회기 정보" sub="번호·날짜·시간·장소" />
            <HubRow to="#transcript" icon="전" title="전사" sub="보기 · 화자 표시 · 줄 고치기" />
            <HubRow
              to={`/sessions/${session.id}/verify`}
              icon="검"
              title="전사 검증"
              sub="두 기기 전사 비교 · 확인할 줄"
            />
            <HubRow to="#memo" icon="메" title="상담자 메모" />
            <HubRow
              to={`/sessions/${session.id}/ai`}
              icon="AI"
              title="AI 분석"
              sub="보낸 자료 · 받은 결과 · 2차 검토"
            />
            <HubRow to="#analysis" icon="분" title="회기 분석" sub="정서·인지·행동·관계·개입" />
            <HubRow to="#risk" icon="위" title="위험 평가" />
            <HubRow to="#goals" icon="목" title="목표 진행" />
            <HubRow to="#evidence" icon="근" title="근거" sub="내담자 진술 · 상담자 관찰 · 가설" />
            <HubRow
              to="#journal"
              icon="기"
              title="상담기록지"
              sub={journal ? (journal.status === 'approved' ? '승인됨 · Word로 저장' : '초안') : '아직 없음'}
            />
            <HubRow to="#ranges" icon="구" title="중요 구간 · 대화록 · 축어록" />
          </nav>
          <div className="stack hub-content">
            <details className="card" id="info">
              <summary>
                <strong>회기 정보 고치기</strong>{' '}
                <span className="muted small">
                  상담 날짜 {session.sessionDate} · {session.durationMinutes ?? '-'}분
                  {session.location ? ` · ${session.location}` : ''}
                </span>
              </summary>
              <SessionInfoFields session={session} />
              <p className="muted small">
                바꾼 날짜는 상담기록지·보고서를 새로 보거나 만들 때 반영됩니다. 이미 만든 사례보고서는 '현재 시점
                사례보고서 만들기'로 다시 만드세요.
              </p>
            </details>
            <div id="transcript">
              <TranscriptSection session={session} onChanged={reload} />
            </div>
            <div id="memo">
              <MemoBox sessionId={session.id} />
            </div>
            <div className="card" id="ai">
              <h2>AI 분석</h2>
              <p className="muted small">
                {session.processingState.aiAnalysisImported
                  ? 'AI 결과가 제안으로 반영되었습니다. 아래 각 항목에서 검토하세요.'
                  : '분석자료를 복사해 외부 AI 프로젝트에 붙여넣고, 결과를 붙여넣으면 아래 항목과 상담기록지 초안에 제안으로 채워집니다.'}
              </p>
              <Link
                className={`button ${session.processingState.aiAnalysisImported ? '' : 'primary'}`}
                to={`/sessions/${session.id}/ai`}
              >
                {session.processingState.aiAnalysisImported ? 'AI 결과·2차 검토' : 'AI 분석하기'}
              </Link>
            </div>
            <div id="analysis">
              <AnalysisSection session={session} />
            </div>
            <div id="risk">
              <RiskSection sessionId={session.id} />
            </div>
            <div id="goals">
              <GoalProgressSection session={session} />
            </div>
            <div id="evidence">
              <EvidenceSection sessionId={session.id} />
            </div>
            <div className="card" id="journal">
              <h2>상담기록지</h2>
              {journal ? (
                <Link className="button primary" to={`/sessions/${session.id}/journal`}>
                  {journal.status === 'approved' ? '상담기록지 보기' : '상담기록지 검토·승인'}
                </Link>
              ) : (
                <button
                  type="button"
                  className="primary"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await svc.createJournalDraft(session.id);
                      navigate(`/sessions/${session.id}/journal`);
                    })
                  }
                >
                  상담기록지 초안 만들기
                </button>
              )}
              <p className="muted small">
                위 분석·위험·목표 내용과 내담자 정보에서 자동으로 채웁니다. 같은 내용을 다시 입력하지 않아도 됩니다.
              </p>
            </div>
            <div id="ranges">
              <DialogueMaker session={session} />
            </div>
            {session.status === 'approved' && <Completion session={session} />}
          </div>
        </div>
      )}
      {session.status !== 'approved' && <DeleteDraft session={session} />}
    </section>
  );
}

/** 목차 한 줄: #칸이면 그 칸으로, 주소면 그 화면으로 */
function HubRow({ to, icon, title, sub }: { to: string; icon: string; title: string; sub?: string }) {
  const body = (
    <>
      <span className="li-icon">{icon}</span>
      <span className="li-main">
        <span className="li-title">{title}</span>
        {sub && <span className="li-sub">{sub}</span>}
      </span>
      <span className="li-go">›</span>
    </>
  );
  return to.startsWith('#') ? (
    <a
      className="list-item"
      href={to}
      onClick={(e) => (
        e.preventDefault(),
        document.getElementById(to.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      )}
    >
      {body}
    </a>
  ) : (
    <Link className="list-item" to={to}>
      {body}
    </Link>
  );
}

function Completion({ session }: { session: Session }) {
  const p = session.processingState;
  const row = (ok: boolean, text: string) => (
    <li className={ok ? 'done' : ''}>
      {ok ? '✓' : '·'} {text}
    </li>
  );
  return (
    <div className="card done-card">
      <h2>회기 완료</h2>
      <ul className="checklist">
        {row(p.filesSaved, '자료 저장')}
        {(session.samsungTranscriptId || session.appleTranscriptId) && row(p.workingTranscriptCreated, '전사 처리')}
        {(session.samsungTranscriptId || session.appleTranscriptId) && row(p.verificationComplete, '중요 구간 확인')}
        {row(p.journalApproved, '상담기록지 승인')}
        {row(p.nextSessionBriefCreated, '다음 회기 준비 생성')}
      </ul>
      <Link className="button primary" to={`/clients/${session.clientId}/brief?target=${session.sessionNumber + 1}`}>
        다음 회기 준비 보기
      </Link>
    </div>
  );
}

function DeleteDraft({ session }: { session: Session }) {
  const svc = useService();
  const navigate = useNavigate();
  const [armed, setArmed] = useState(false);
  return (
    <details className="muted">
      <summary>이 회기 삭제 (잘못 만들었거나 오류가 나서 다시 만들 때)</summary>
      <p className="small">
        이 회기와 여기서 나온 전사·메모·분석·근거·AI 결과·기록지 초안이 함께 지워집니다. 지운 뒤 같은 회기 번호로 새로
        만들 수 있습니다. 원본 전사 파일은 기기에 있으니 다시 올리면 됩니다.
      </p>
      {armed ? (
        <div className="row">
          <button
            type="button"
            className="danger"
            onClick={() => void svc.deleteSession(session.id).then(() => navigate(`/clients/${session.clientId}`))}
          >
            삭제 확인 (기록은 이력에 남습니다)
          </button>
          <button type="button" onClick={() => setArmed(false)}>
            취소
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setArmed(true)}>
          삭제
        </button>
      )}
    </details>
  );
}
